import TestClubInvitation from '../../shared/TestClubInvitation';
import { managersHero, managersIntroduction, managersQuote, managersQuoteName, managersQuoteTitle, managersItems, managersTimeline } from './managersContent.js';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Flex, Col, Row, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { setHeroTitle, setHeroContent, setHeroImage } from '../../redux/hero.slice'
const { Title, Paragraph } = Typography;

import Quote from '../../shared/Quote/Quote'
import Conatiner from '../../shared/Conatiner/Container';

import clubManagersImg from '../../assets/club-managers-new.png';
import playersImg from '../../assets/players.png';
import FeatureList from '../../shared/FeatureList';
import Timeline from '../../shared/Timeline/Timeline';
import RelatedPulseLinks, { relatedPulseArticles } from '../../shared/RelatedPulseLinks';
import { PAGE_METADATA } from '../../pageMetadata';
import PageMetadata from '../../shared/PageMetadata';

import useMobileCheck from '../../mobile';

const FeaturesPage = () => { 
  const { persona } = useParams();
  const metadata = persona === 'managers'
    ? PAGE_METADATA['/features/managers']
    : PAGE_METADATA['/features/players'];

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setHeroImage(false));
    if ( persona === "managers" ) { 
      dispatch(setHeroTitle(`For Club Managers`));
      dispatch(setHeroContent(managersHero));
    } else { 
      dispatch(setHeroTitle(`For Club Players`));
      dispatch(setHeroContent(`You want to concentrate on playing, not the politics of who plays whom and you certainly don't want the responsibility of trying to make fair games. 
You just want to play, and easily pay for sessions you attend. If that's you, then you need ShuttleOps. It'll take the guess work out of games and give you stats and information
about your games in the process.`));
    }
  }, [persona, dispatch]);


  const { isMobile } = useMobileCheck();



  const playerItems = [
    {
      heading: true,
      title: 'Why Players Love ShuttleOps',
    },
    {
      title: 'Fair Game Allocation',
      description:
        'Say goodbye to unfair rotations. The Digital Peg Board ensures you get a balanced number of games, rotating partners and opponents to keep things fun and competitive.',
    },
    {
      title: 'Track Your Progress',
      description:
        'Access personal stats like games played, partners, opponents, and attendance over time. Stay on top of your game and see how you’re improving.',
    },
    {
      title: 'Play More, Wait Less',
      description:
        'The automated court assignment system means faster transitions and less downtime between games. Get more games in every session.',
    },
    {
      title: 'Join Multiple Clubs',
      description:
        'Easily switch between clubs if you play at more than one. Your stats and payments stay organised by club for clarity and convenience.',
    },
    {
      title: 'Optional Registration',
      description:
        'You don’t need to register to play—but if you do, you’ll unlock stats, notifications, and personalized access to your playing history.',
    }
  ];
  



  const playerTimeline = [
    {
      title: "Get Invited to Your Club",
      description: "Once your club is using ShuttleOps, you’ll receive an invite via email. You don’t need to register, but doing so unlocks extra features like stats, payments, and personalized session updates."
    },
    {
      title: "Register to Unlock Features",
      description: "Create your account in just a few taps. With registration, you can track your games, view payment history, and receive session reminders—all from your own dashboard."
    },
    {
      title: "Play in Fair, Balanced Games",
      description: "The Digital Peg Board automatically rotates players across courts based on availability and past matchups, ensuring fair playtime and more enjoyable, competitive games for everyone."
    },
    {
      title: "Check Your Stats and History",
      description: "See how often you’ve played, who you’ve teamed up with, and which sessions you’ve attended. Stats are automatically updated and available anytime on your profile."
    },
    {
      title: "Make Easy Payments",
      description: "Pay for sessions or subscriptions directly through the platform. ShuttleOps keeps track of what you've paid and what's due—no more confusion or awkward cash exchanges."
    },
    {
      title: "Stay Connected",
      description: "Never miss a session. Get notified about new games, time changes, or club announcements through the built-in messaging system. Everything’s in one place."
    }
  ];
  

  return ( 
    <>
      <PageMetadata metadata={metadata} />
      <Flex vertical
        style={{
          paddingTop: 20,
          paddingBottom: 20
        }}
        gap={20}>
      <Conatiner
        colour="transparent"
        style={{ 
          pading: 20 
        }}
        >
        <Row
          align="top"
          style={{ 
            padding: 20 
          }}
        >
          <Col 
            xs={{ span: 24, order: 2 }} 
            sm={{ span: 12, order: 1 }}
            >
            <img
              src={persona === "managers" ? clubManagersImg : playersImg }
              alt="Club managers"
              className="feature-img"
              style={{ 
                maxWidth: '35rem'
              }}
            />
          </Col>
          <Col 
          xs={{ span: 24, order: 1 }} sm={{ span: 12, order: 2 }}>
            <Flex gap={16} vertical
              style={{ 
                marginBottom: isMobile ? '20px' : '0px',
                padding: 20
              }}
              >
              { persona === "managers" && <>
                <Title className="pegboard-highlight-title">
                  Why Club Managers Love ShuttleOps
                </Title>
                <Paragraph className="pegboard-highlight-description">
                  {managersIntroduction}
                </Paragraph>
                <RelatedPulseLinks
                  articles={relatedPulseArticles.managers}
                  label="Useful next steps"
                  links={[
                    {
                      title: 'Run your first ShuttleOps club session',
                      path: '/guides/first-club-session',
                    },
                    {
                      title: 'Playwaze vs ShuttleOps for badminton clubs',
                      path: '/compare/playwaze-alternative',
                    },
                    {
                      title: 'Spond vs ShuttleOps for badminton clubs',
                      path: '/compare/spond-alternative',
                    },
                    {
                      title: 'SportMember vs ShuttleOps for badminton clubs',
                      path: '/compare/sportmember-alternative',
                    },
                    {
                      title: 'Badminton club payment software',
                      path: '/badminton-club-payment-software',
                    },
                  ]}
                />
                <Quote
                  quote={managersQuote}
                  name={managersQuoteName}
                  title={managersQuoteTitle}
                  stars={5}
                  />
              </> }

              { persona === "players" && <>
                <Title className="pegboard-highlight-title">
                  Why Players Love ShuttleOps
                </Title>
                <Paragraph className="pegboard-highlight-description">
                  Players love ShuttleOps because it puts them at the heart of their club experience. The platform’s standout feature — the Digital Peg Board — ensures 
                  fair and transparent court assignments, meaning no more waiting around or being overlooked. Every player gets a balanced mix of games, matched 
                  with different partners and opponents, which not only keeps sessions fun and social, but also helps improve skill through varied play. 
                  It’s smart matchmaking without the fuss.
                </Paragraph>

                <Paragraph className="pegboard-highlight-description">
                ShuttleOps also gives players real value through personal stats, attendance tracking, and payment history, all easily accessible through a modern, 
                mobile-friendly interface. Registered users can view their match records, see how often they’ve played, and even monitor their performance trends 
                over time. Add to that seamless communication with club admins, automatic session notifications, and hassle-free payments, and it’s clear why 
                players quickly feel like ShuttleOps is built for them. Whether you're chasing improvement or just want better-organised, more enjoyable club nights, 
                ShuttleOps makes it easier to play, connect, and grow.
                </Paragraph>
                <RelatedPulseLinks articles={relatedPulseArticles.players} />
                <Quote
                  quote={"ShuttleOps has completely transformed the way we manage our badminton club. Scheduling, payments, and attendance tracking are now effortless, allowing us to focus on what truly matters-building a thriving community and enhancing player experience."}
                  name={"Mark Jensen"}
                  title={"Club Manager, Elite Badminton Academy"}
                  stars={4}
                  />
              </> }
            </Flex>
          </Col>
        </Row>
      </Conatiner>
      
      
      
      <Conatiner
        yellow={true}
        >
        <Flex 
          wrap
          style={{ 
            padding: isMobile ? '10px' : '40px'
          }}
          >
          <FeatureList items={ persona === "managers" ? managersItems : playerItems } type="blocks"/>
        </Flex>
      </Conatiner>
      <Conatiner
        white={true}
        colour="white"
        >
        <Flex 
          wrap
          style={{ 
            padding: isMobile ? '10px' : '0px 40px 40px 40px'
          }}
          >
          <Timeline timeline={ persona === "managers" ? managersTimeline : playerTimeline } title="How It Works"/>
        </Flex>
      </Conatiner>
      {persona === "managers" && <TestClubInvitation />}
      </Flex>
    </>
  )
}

export default FeaturesPage;
